import React from 'react';
import { useRouter } from 'next/router';
import { Card, Table } from '@avila-tek/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { Hit } from '@algolia/client-search';
import { PageInfo, Pagination, PayableRequestInfo } from '../../models';
import { ActiveDotCell } from '../shared';
import { useAlgoliaSearch } from '../../hooks';
import { AlgoliaHitToObject } from '../../lib';
import { orderPaymentStatusTranslate } from '../../static/orderStatus';

const columnHelper = createColumnHelper<PayableRequestInfo>();

const columns = [
  columnHelper.accessor('ally', {
    header: 'Aliado',
    cell: (info) => (
      <div className="flex flex-col items-start ">
        <p className="font-bold">{info.row.original.ally.name}</p>
        <p className="text-gray-500">J {info.row.original.ally.dni}</p>
      </div>
    ),
  }),

  columnHelper.accessor('ally', {
    header: 'Correo',
    cell: (info) => info.row.original.ally.email,
  }),

  columnHelper.accessor('orderPayments', {
    header: '# Gift Cards',
    cell: (info) => {
      if (info.row.original.orderPayments.length === 0) {
        return 'N/A';
      }
      return info.row.original.orderPayments.length;
    },
  }),

  columnHelper.accessor('total', {
    header: 'Deuda',
    cell: (info) => `$${info.row.original.total}`,
  }),

  columnHelper.display({
    header: 'Estatus',
    id: 'status',
    cell: (info) => (
      <ActiveDotCell
        active={info.row.original.total === 0}
        text={
          orderPaymentStatusTranslate[
            info.row.original.total === 0 ? 'paid' : 'pending'
          ]
        }
      />
    ),
  }),
];

interface PayableTableProps {
  data: Pagination<PayableRequestInfo>;
  pagination: Partial<PageInfo>;
  setPagination: React.Dispatch<React.SetStateAction<Partial<PageInfo>>>;
}

function PayableTable({ data, pagination, setPagination }: PayableTableProps) {
  const router = useRouter();
  const [accountsData, setAccountsData] = React.useState(data.items);

  const onClickRow = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const { id } = e.currentTarget.dataset;
    router.push(`/app/payable/${id}`);
  };

  const [searchText, setSearchText] = React.useState<string | number>('');
  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const { fetchData } = useAlgoliaSearch<PayableRequestInfo>({
    // PREGUNTAR COMO BUSQUEDA EN ALGOLIA
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: [
      'ally.name',
      'ally.email',
      'ally.dni',
      'total',
      'orderPayments.paymentStatus',
    ],
    index: 'accounts',
  });

  const handleGoToPage = (value: number) => {
    if (searchText) {
      setPageInfoAlgolia({ ...pageInfoAlgolia, currentPage: value - 1 });
    } else {
      setPagination({ ...pagination, currentPage: value });
    }
  };

  React.useEffect(() => {
    async function syncData() {
      if (searchText) {
        const { items } = await fetchData({
          searchText: searchText.toString(),
          newPage: pageInfoAlgolia.currentPage,
        });
        setAccountsData(
          AlgoliaHitToObject<PayableRequestInfo>(
            items as Hit<PayableRequestInfo>[]
          )
        );
      } else {
        setAccountsData(data.items);
      }
    }
    syncData();
  }, [pageInfoAlgolia.currentPage, searchText, pagination]);

  return (
    <Card className="overflow-x-auto p-5">
      <Table<PayableRequestInfo>
        data={accountsData}
        columns={columns}
        href="/app/allies/create"
        manualPagination
      >
        <Table.Header className="p-6 w-full flex justify-between">
          <Table.SearchInput
            manualFilter
            value={searchText.toString()}
            onChange={(value) => {
              setSearchText(value);
            }}
          />
        </Table.Header>
        <Table.Content<PayableRequestInfo> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination
            handleGoToPage={handleGoToPage}
            paginationInfo={{
              count: data.count,
              pageInfo: searchText
                ? {
                    ...pageInfoAlgolia,
                    currentPage: pageInfoAlgolia.currentPage + 1,
                  }
                : data.pageInfo,
            }}
            className="ml-auto"
          />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default PayableTable;
